import React, { createContext, useContext } from 'react'
import { CMSSectionBase } from '../../../lib/types'

const CmsSectionTextColorContext = createContext<string | undefined>(undefined)

/** Resolves Sanity `backgroundColor.hex` for any CMSSectionBase-shaped block */
export const resolveCmsSectionBackgroundHex = (section?: CMSSectionBase): string | undefined =>
  section?.backgroundColor?.hex

export const CmsSectionTextColorProvider = ({
  backgroundColor,
  children,
}: {
  backgroundColor?: string
  children: React.ReactNode
}) => (
  <CmsSectionTextColorContext.Provider value={backgroundColor}>
    {children}
  </CmsSectionTextColorContext.Provider>
)

export const useCmsSectionTextColor = () => useContext(CmsSectionTextColorContext)

export const getCmsSectionShellStyle = (
  backgroundColor?: string,
): React.CSSProperties | undefined =>
  backgroundColor
    ? ({
        '--opportunity-cms-text-background': backgroundColor,
        '--opportunity-cms-text-padding': '18px 20px',
        '--opportunity-cms-text-radius': '16px',
      } as React.CSSProperties)
    : undefined

export const getCmsSectionShellClassName = (baseClassName: string, backgroundColor?: string) =>
  `${baseClassName}${backgroundColor ? ' opportunity-cms-section--has-text-fill' : ''}`

/** Use on nested rich-text wrappers (e.g. card grids) inside CMS section bodies */
export const CMS_TEXT_BLOCK_CLASS =
  'opportunity-cms-section-text opportunity-cms-section-text--filled'

type CmsSectionTextProps = {
  children: React.ReactNode
  className?: string
  dataTestId?: string
  /** Override section context when rendering outside a shell provider */
  backgroundColor?: string
  as?: 'div' | 'section'
}

const CmsSectionText = ({
  children,
  className = '',
  dataTestId,
  backgroundColor,
  as: Tag = 'div',
}: CmsSectionTextProps) => {
  const contextColor = useCmsSectionTextColor()
  const fillColor = backgroundColor ?? contextColor

  return (
    <Tag
      className={`opportunity-cms-section-text${
        fillColor ? ' opportunity-cms-section-text--filled' : ''
      }${className ? ` ${className}` : ''}`}
      data-testid={dataTestId}
    >
      {children}
    </Tag>
  )
}

export { CmsSectionText }
